import React from "react";
import TableContainer from "../../../ui-kit/data-table/table/table-container";
import krookieJournalIcon from "../../../../images/krookie-journal.svg";
import TableColumn from "../../../ui-kit/data-table/table-column/table-column";
import { useAppSelector } from "../../../../hooks/useAppSelector";
import { useAppDispatch } from "../../../../hooks/useAppDispatch";
import {
  setData,
  setReceiveNotifications,
  setSelectedRow,
} from "../../../../services/reducers/utilities-journal";
import Checkbox from "../../../ui-kit/checkbox/checkbox";
import styles from "./utilities-journal.module.scss";
import Button from "../../../ui-kit/button/button";
import exportIcon from "../../../../images/export.svg";
import StageTag from "../../../../shared/stage-tag/stage-tag";
import StatusCell from "../../../../shared/status-cell-render/status-cell";
import { useNavigate } from "react-router-dom";
import { viewNotification } from "../../../../services/api";
import { ExportToExcel } from "../../../../utils/export-to-excel";
import { Roles } from "../../../../types/roles";
import { ProjectStatus } from "../../../../interfaces/utilities";
import { useTranslation } from "react-i18next";

const UtilitiesJournal = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { fullData, data, totalDataSize, isLoading, receiveNotifications } =
    useAppSelector((state) => state.masterUtilities);
  const { user } = useAppSelector((state) => state.user);
  const navigate = useNavigate();

  const isDepartmentSpecialist = user?.roles.includes(
    Roles.DepartmentSpecialist,
  );

  const handleClickRow = async (row: any) => {
    const foundRow = fullData.find((item) => item.id === row.id);
    if (!foundRow) return;

    if (foundRow.notificationId != null) {
      try {
        await viewNotification(foundRow.notificationId);
      } catch (e) {
        console.error(e);
      }
    }

    dispatch(setSelectedRow(foundRow));
    navigate("/utilitiesMaster");
  };

  const handleExportExcel = () => {
    const columns = {
      Num: 8,
      Stage: 20,
      Status: 20,
      Amount: 10,
    };

    const dataExcel = [
      { Num: 1, Stage: "Design stage", Status: ProjectStatus.Rejected },
      { Num: 2, Stage: "Design stage", Status: ProjectStatus.Approved },
      { Num: 3, Stage: "Design stage", Status: ProjectStatus.NeedApproval },
      { Num: 4, Stage: "Design stage", Status: ProjectStatus.NotInitiated },
      { Num: 5, Stage: "As-Built stage", Status: ProjectStatus.Rejected },
      { Num: 6, Stage: "As-Built stage", Status: ProjectStatus.Approved },
      { Num: 7, Stage: "As-Built stage", Status: ProjectStatus.NeedApproval },
    ];

    const countMap = new Map<string, number>();

    const normalizeStage = (s: string) =>
      s.trim() === "As-Built" ? "As-Built stage" : s.trim();

    const getKey = (stage: string, status: ProjectStatus | string) =>
      `${stage.toLowerCase()}|${status.toString().toLowerCase()}`;

    fullData.forEach((item) => {
      const stage = normalizeStage(item.stage);
      const key = getKey(stage, item.status);
      countMap.set(key, (countMap.get(key) ?? 0) + 1);

      if (
        stage.toLowerCase() === "as-built stage" &&
        item.status === ProjectStatus.NotInitiated
      ) {
        const designApprovedKey = getKey(
          "Design stage",
          ProjectStatus.Approved,
        );
        countMap.set(
          designApprovedKey,
          (countMap.get(designApprovedKey) ?? 0) + 1,
        );
      }
    });

    const dataExcelWithAmount = dataExcel.map((item) => {
      const key = getKey(item.Stage, item.Status);
      return { ...item, Amount: countMap.get(key) ?? 0 };
    });

    ExportToExcel(
      columns,
      dataExcelWithAmount,
      "UtilitiesReport",
      "UtilitiesReport.xlsx",
    );
  };

  return (
    <TableContainer
      idField="id"
      tableName={t("utilities-journal.tableName")}
      tableIcon={krookieJournalIcon}
      dataSource={fullData}
      rowHeight={88}
      dateTemplate="MM/DD/YYYY hh:mm pm"
      debounceMs={1000}
      data={data}
      totalDataSize={totalDataSize}
      setData={(payload) => dispatch(setData(payload))}
      placeholderSearch={t("utilities-journal.searchPlaceholder")}
      substringFields={[
        "contractorAndDescriptionOfWork",
        "statusAndModificationDate",
        "workPermitNumber",
        "stage",
      ]}
      visibleFilter={false}
      isLoading={isLoading}
      headerContent={() => (
        <div className={styles.checkboxWrapper}>
          <Checkbox
            checked={receiveNotifications}
            onChange={(checked) => dispatch(setReceiveNotifications(checked))}
            classes={{
              label: styles.label,
            }}
            label={t("utilities-journal.receiveNotifications")}
          />
        </div>
      )}
      footerContent={() =>
        isDepartmentSpecialist ? (
          <div className={styles.footerContent}>
            <Button
              disabled={isLoading}
              onClick={handleExportExcel}
              className={styles.btn}
              type="withIcon"
            >
              {t("utilities-journal.createReport")}
              <img src={exportIcon} alt="Create Report" />
            </Button>
          </div>
        ) : null
      }
      visibleSelect={false}
      onRowClick={(row) => handleClickRow(row)}
      getRowColor={(row) =>
        receiveNotifications && row.notificationId != null
          ? "#E59E6D0D"
          : undefined
      }
    >
      <TableColumn
        dataField="workPermitNumber"
        caption={t("utilities-journal.columns.workPermitNumber")}
        style={{ width: "140px" }}
      />
      <TableColumn
        dataField="contractorAndDescriptionOfWork"
        caption={t("utilities-journal.columns.contractorDescription")}
        renderCell={(value: string) => {
          const [contractor = "", description = ""] = value.split("\n");
          return (
            <div className={styles.textPairColumn}>
              <span className={styles.primaryText}>{contractor}</span>
              <span className={styles.secondaryText}>{description}</span>
            </div>
          );
        }}
        style={{ width: "380px" }}
      />
      <TableColumn
        dataField="workPermitStatus"
        caption={t("utilities-journal.columns.workPermitStatus")}
        renderCell={(value: string) => {
          const [workPermitStage = "", buildingPermitStage = ""] =
            value.split("\n");
          return (
            <div className={styles.textPairColumn}>
              <span className={styles.primaryText}>{workPermitStage}</span>
              <span className={styles.secondaryText}>
                {buildingPermitStage}
              </span>
            </div>
          );
        }}
        style={{ width: "160px" }}
      />
      <TableColumn
        dataField="duration"
        caption={t("utilities-journal.columns.duration")}
        renderCell={(value: string) => {
          const [issueDate = "", expireDate = ""] = value.split("\n");
          return (
            <div className={styles.textPairColumn}>
              <span>{issueDate}</span>
              <span>{expireDate}</span>
            </div>
          );
        }}
        style={{ width: "140px" }}
      />
      <TableColumn
        dataField="stage"
        caption={t("utilities-journal.columns.stage")}
        renderCell={(value: string) => <StageTag stage={value} />}
        style={{ width: "140px" }}
      />
      <TableColumn
        dataField="statusAndModificationDate"
        caption={t("utilities-journal.columns.statusModificationDate")}
        renderCell={(value: string) => {
          const [status = "", description = ""] = value.split("\n");

          return (
            <StatusCell
              status={status}
              description={description}
              classes={{ description: styles.statusDesc }}
            />
          );
        }}
      />
    </TableContainer>
  );
};

export default UtilitiesJournal;
